/* tslint:disable */
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { NodeType } from '../../classes/tree/IBaseNode';
import { NearbyAssetNode } from '../../classes/tree/nodes/NearbyAssetNode';
import { Tree } from '../../classes/tree/tree';
import { environment } from '../../environment';
import { ESRI } from '../../esri-loader-util';
import { BooleanConverter } from '../../inputConverter';
import { DistanceUnits } from '../../selectionOptions';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { LayerService } from '../../services/layer.service';
import { NearbyService } from '../../services/nearby.service';
import { ILayerTableChild, IMCMapView, LogLevel, MapCoreLayer } from '../../types';
import { BaseComponent } from '../base.component';


let Graphic: __esri.GraphicConstructor;
let PictureMarkerSymbol: __esri.PictureMarkerSymbolConstructor;
let WebMercatorUtils: __esri.webMercatorUtils;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-nearby-ng',
    styleUrls: ['../sharedStyles/base.scss', './nearby.component.scss'],
    templateUrl: './nearby.component.html'
})
export class NearbyComponent extends BaseComponent implements OnInit, OnDestroy {
    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly nearbyService: NearbyService,
        private readonly cdr: ChangeDetectorRef) {
        super(_vcr, true);
    }

    @Input() set graphic(value: NonNullable<__esri.Graphic>) {
        if (this._graphic !== value) {
            this._graphic = value;
            if (value) {
                this.executeNearbyAssetsTask();
            }
        }
    }
    get graphic(): NonNullable<__esri.Graphic> { return this._graphic; }
    private _graphic: NonNullable<__esri.Graphic>;

    @Input() mapViewRef: IMCMapView;
    @Input() set active(value: boolean) {
        value = BooleanConverter(value);
        if (this._active !== value) {
            value ? this.activate() : this.deactivate();
            this.activeChange.emit(value);
            this._active = value;
        }
    }
    get active(): boolean { return this._active; }
    private _active: boolean = false;

    @Output() public readonly activeChange = new EventEmitter<boolean>();
    @Output() public readonly openStreetViewWithCoordinates = new EventEmitter<any>();
    @Output() public readonly openVideoLogWithCoordinates = new EventEmitter<any>();
    @Output() readonly status: EventEmitter<string> = new EventEmitter();

    NodeType = NodeType;

    public asc: boolean = true;
    public currentNode: Tree<NearbyAssetNode>;
    public distanceUnits = DistanceUnits;
    public distanceDefaultPoly: number = 0;
    public distanceDefaultPtLn: number = 1000;
    public distance: number = this.distanceDefaultPtLn;
    public distanceDefaultUnit: __esri.LinearUnits = 'feet'
    public distanceMaxDefault: number = 26400;
    public distanceMax: number = this.distanceMaxDefault;
    public areaMax: number = 20; 
    public groups: any;
    public showPrimary: boolean = true;
    public showSecondary: boolean = true;

    private _distanceUnit: __esri.LinearUnits = this.distanceDefaultUnit;
    get distanceUnit() {
        return this._distanceUnit;
    }
    set distanceUnit(unit: __esri.LinearUnits) {
        this.distance = Math.round(this.distance / this.convertMeter[this._distanceUnit] * this.convertMeter[unit] * 1000) / 1000;
        this.distanceMax = Math.round(this.distanceMax / this.convertMeter[this._distanceUnit] * this.convertMeter[unit] * 1000) / 1000;
        this._distanceUnit = unit;
    }
    public orderBy: string;

    public loading: boolean;
    public loaderText: string = 'loading...';
    private openedDropdown: any;
    private assets: ILayerTableChild[];
    private convertMeter = { feet: 3.28084, miles: 0.000621371, kilometers: 0.001, meters: 1, 'nautical-miles': 0.000539957, yards: 1.09361 };

    private highlightColor: string = 'rgba(125, 247, 229, 0.75)';
    private highlightSymbols = {
        point: { type: 'simple-marker', outline: { color: this.highlightColor } },
        polyline: { type: 'simple-line', width: 5, color: this.highlightColor },
        polygon: { type: 'simple-fill' },
        multipoint: { type: 'simple-marker', outline: { color: this.highlightColor } },
        default: { type: 'simple-marker' }
    };
    private readonly nearbyAssetColor: string = '#2887c7';
    private readonly nearbyAssetSymbols = {
        point: { type: 'simple-marker', color: this.nearbyAssetColor },
        polyline: { type: 'simple-line', width: 3, color: this.nearbyAssetColor },
        polygon: { type: 'simple-fill', color: this.nearbyAssetColor },
        multipoint: { type: 'simple-marker', color: this.nearbyAssetColor },
        default: { type: 'simple-marker', color: this.nearbyAssetColor }
    };
    // TODO: It would be better to have a local png pin
    private readonly nearbyAssetPointSymbol = {
        url: 'https://static.arcgis.com/images/Symbols/Shapes/BluePin1LargeB.png',
        height: 32,
        width: 32,
        yoffset: 12
    };
    public _searchText: string;
    get searchText() {
        return this.currentNode ? this.currentNode.data.searchText : this._searchText;
    }
    set searchText(value: string) {
        if (this.currentNode) {
            this.currentNode.data.searchText = value;
        }
        this._searchText = value;
    }
    private tree: Tree<NearbyAssetNode>;

    async ngOnInit() {
        WebMercatorUtils = await ESRI.WebMercatorUtils;
    }

    private activate() {
    }

    async ngOnDestroy() {
        this.deactivate();
    }

    private deactivate() {
        Object.values(this.nearbyService.nearbyLayers).forEach(title => this.nearbyService.removeNearbyLayer(title));
    }

    public async onMouseEnter(node: Tree<NearbyAssetNode>) {
        this.highlightNode(node);
    }

    public onMouseLeave() {
        this.nearbyService.setNearbyLayer(this.nearbyService.nearbyLayers.highlight, []);
    }

    onDistanceChange(distance) {
        if (distance > this.distanceMax) {
            this.distance = this.distanceMax;
        }
        if (distance < 0) {
            this.distance = 0
        }
    }

    toggleDropdown(event?) {
        if (event) {
            const target = event.target || event.srcElement || event.currentTarget;
            const dropdown = target.offsetParent.nextElementSibling || target.nextElementSibling;

            dropdown.classList.toggle('show');
            this.openedDropdown = dropdown;
            window.addEventListener('mousedown', this.outsideClickHandler);
            window.addEventListener('dragstart', this.outsideClickHandler);
            this.openedDropdown.addEventListener('mousedown', this.insideClickHandler)
        } else {
            this.openedDropdown.classList.toggle('show');
            window.removeEventListener('mousedown', this.outsideClickHandler);
            window.removeEventListener('dragstart', this.outsideClickHandler);
            this.openedDropdown.removeEventListener('mousedown', this.insideClickHandler);
            this.openedDropdown = null;
        }
    }

    private readonly outsideClickHandler = () => {
        this.toggleDropdown();
    }

    private readonly insideClickHandler = (e: MouseEvent) => {
        e.stopPropagation();
    }

    toggleAccordion(event) {
        const target = event.target || event.srcElement || event.currentTarget;
        const panel = target.nextElementSibling;
        target.classList.toggle('active');
        if (panel.style.display === "block") {
            panel.style.display = "none";
        } else {
            panel.style.display = "block";
        }
    }

    public onLinkOpen() {
        this.toggleDropdown();
    }

    public onVideoLogOpen(node: Tree<NearbyAssetNode>) {
        this.openVideoLogWithCoordinates.emit(node.data.lngLat);
        this.toggleDropdown();
    }

    public onStreetViewOpen(node: Tree<NearbyAssetNode>) {
        this.openStreetViewWithCoordinates.emit(node.data.lngLat);
        this.toggleDropdown();
    }

    public navigateBack(node: Tree<NearbyAssetNode>) {
        node ? this.navigateToNode(node, this.assets) : this.active = false;
    }

    public async bufferChanged() {
        await this.executeNearbyAssetsTask();
    }

    clearFilter() {
        this.searchText = '';
    }

    private async executeNearbyAssetsTask() {
        try {
            this.loading = true;
            this.status.emit('loading');
            let graphics: __esri.Graphic[] = [];
            switch (this.graphic.geometry.type) {
                case 'point':
                    if (this.graphic.clone) {
                        PictureMarkerSymbol = await ESRI.PictureMarker;
                        const pin = this.graphic.clone();
                        pin.symbol = new PictureMarkerSymbol(this.nearbyAssetPointSymbol);
                        graphics = [pin];
                    }
                    break;
                case 'polygon':
                    this.distance = this.distance === this.distanceDefaultPtLn ? 0 : this.distance;
                    break;
                default:
                    break;
            }
            this.nearbyService.setNearbyLayer(this.nearbyService.nearbyLayers.mapPin, graphics);
            this.loaderText = 'Setting nearby searching area...';
            const buffers = await this.nearbyService.getBuffers(this.distance, this.distanceUnit, [this.graphic.geometry]);
            const totalArea = buffers.reduce((area, buffer) => {
                area += buffer.area
                return area;
            }, 0);

            if (totalArea > this.areaMax) {
                window.alert('Nearby Assets Search Area too large.');
            } else {
                const buffer = buffers.map(b => b.graphic);
                this.tree = new Tree<NearbyAssetNode>(new NearbyAssetNode({ name: 'Assets' }));
                this.loaderText = 'Searching for nearby assets...';
                this.assets = await this.nearbyService.getApplicationNearbyAssets(environment.appId);
                const selectionLayer = await this.nearbyService.setNearbyLayer(this.nearbyService.nearbyLayers.selection, buffer);
                const heightOrWithThreshold = 2000;
                const zoomThreshold = 16;
                this.layerService.zoomToTarget(buffer.flatMap(x => x.geometry), heightOrWithThreshold, zoomThreshold);
                this.loaderText = 'Generating results...';
                await this.navigateToNode(this.tree, this.assets, selectionLayer);
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            this.loading = false;
            this.status.emit('loaded');
        }
    }

    private async navigateToNode(node: Tree<NearbyAssetNode>, assets: ILayerTableChild[], selection: MapCoreLayer = null): Promise<Tree<NearbyAssetNode>> {
        try {
            this.currentNode = node;
            await this.buildTree(node, assets, selection);
            if (node.children.length > 0) {
                this.asc = node.data.asc || true;
                if (node.data.orderBy) {
                    this.orderBy = node.data.orderBy
                } else {
                    const columns = this.nearbyService.getSortableColumns(node);
                    if (columns.length) {
                        if (node.children && node.children.length > 0 && node.children[0].data.nodeType === NodeType.Document) {
                            this.orderBy = 'documentClass';
                        } else {
                            this.orderBy = columns[0].name;
                        }
                    } else {
                        this.orderBy = undefined;
                    }
                }
                this.showPrimary = true;
                this.showSecondary = true;
                this.sortChildren(node, this.orderBy, this.asc);
                this.showAssetsOnMap(node.children);
                this.cdr.detectChanges();
            } else {
                this.currentNode = node.parent; // Didn't find data, return user to parent node
             }
            return this.currentNode;
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        }
    }

    private async buildTree(node: Tree<NearbyAssetNode>, assets: ILayerTableChild[], selection: MapCoreLayer): Promise<boolean> {
        const data = node.data;
        if (!node.children.length) {
            node.loading = true;
            if (data.layerId || data.tableId) { // Layer/Table Node
                let layer;
                if (data.layerId) {
                    // TODO: Consider moving next line to initialization of component and
                    // load all possible layers worth of data at one time? Can we also
                    // fetch the docSources for each layer too there?
                    const layers = await this.layerService.getLayers([data.layerId]);
                    layer = layers.filter(x => x.mapcoreProps.parentId === data.layerId)[0];
                }
                let query;
                const parentRecord = this.nearbyService.findKeyParent(node);
                if (parentRecord) {
                    query = this.nearbyService.getJoinQuery(parentRecord, node);
                } else if (layer) {
                    query = await this.nearbyService.performSpatialQuery(layer, selection);
                }
                if (query) {
                    let columns, records;
                    if (data.layerId) {
                        this.nearbyService.updateLayerFilter(layer, query);
                        ({ columns, records } = await this.nearbyService.getLayerRecords(layer, data.columnList));
                    } else {
                        ({ columns, records } = await this.nearbyService.getTableRecords(data.tableId, query, data.columnList));
                    }
                    data.columns = columns;
                    this.nearbyService.addRecordNodes(node, data.columnList, records);
                    data.enabled = !!node.children.length;
                } else {
                    // no query means the current node shouldn't be shown to the user. Typically due to permissions (for layers)
                    node.remove(node.name);
                }
            } else { // Category/Record/Document Nodes
                const childrenAssets = assets.filter(a => a.parentId === data.id);
                childrenAssets.forEach(asset => node.addChild(new NearbyAssetNode({ name: asset.relationshipName, ...asset })));

                // TODO: There is a bug here somewhere. If one child's buildTree fails I think its rejected
                //       promise throws things off. Simulate by giving one of the children a bad column name
                //       in the LAYER_TABLE_CHILD_XREF table. You can navigate to an ECMS Project child records
                //       chilren tables but when you go back, that same node is now disabled and shouldn't be.
                const tasks = node.children.map(child => this.buildTree(child, assets, selection))
                tasks.push(this.fetchDocuments(node));
                data.enabled = await this.anyTrue(tasks);

            }
            node.loading = false;
        }
        this.cdr.detectChanges();
        return data.enabled;
    }

    private async fetchDocuments(node): Promise<boolean> {
        if (node.data.record) { // Record Node
            node.parent.data.sources = node.parent.data.sources || await this.nearbyService.getDocumentSources(node);
            if (node.parent.data.sources.length) {
                node.addChild(new NearbyAssetNode({ name: 'Documents' }));
                const docNode = node.children.filter(n => n.data.name === 'Documents')[0];
                docNode.loading = true;
                const documents = await this.nearbyService.getDocuments(node, node.parent.data.sources);
                if (documents.length) {
                    await this.nearbyService.addDocumentNodes(docNode, documents);
                    docNode.name = `Documents (${docNode.children.length})`;
                    docNode.data.enabled = true;
                    node.data.enabled = true;
                }
                docNode.loading = false;
            }
        }
        return true;
    }

    private async anyTrue(promises: Promise<boolean>[]): Promise<boolean> {
        const newPromises: Promise<boolean>[] = promises.map(p =>
            new Promise(
                (resolve, reject) => p.then(v => v && resolve(true), reject)
            ));
        newPromises.push(Promise.all(promises).then(() => false));
        return Promise.race(newPromises);
    }

    private async showAssetsOnMap(children: Tree<NearbyAssetNode>[]) {
        Graphic = await ESRI.Graphic;
        const graphics: __esri.Graphic[] = [];
        const defaultSymbol = 'default';
        for (const child of children) {
            const data = child.data;
            if (!data.graphic && data.record && data.nodeType === NodeType.Record) {
                const idx = data.columnList.indexOf('GEOMETRY');
                if (idx >= 0) {
                    const geometries = await this.layerService.convertGeometry([data.record[idx]]);
                    const geometry = geometries[0];
                    data.graphic = new Graphic({
                        geometry,
                        symbol: this.nearbyAssetSymbols[geometry.type] || this.nearbyAssetSymbols[defaultSymbol]
                    });

                    if (geometry.type === 'point') {
                        data.lngLat = WebMercatorUtils.xyToLngLat((geometry as any).x, (geometry as any).y);
                    } else {
                        data.lngLat = WebMercatorUtils.xyToLngLat((geometry as any).paths[0][0][0], (geometry as any).paths[0][0][1]);
                    }
                }
            }
            if (data.graphic) {
                graphics.push(data.graphic);
            }
        }
        const layer = await this.nearbyService.setNearbyLayer(this.nearbyService.nearbyLayers.assets, graphics);
        if (graphics.length > 0) {
            this.layerService.moveLayerToTheTop(layer);
        }
    }

    public async highlightNode(node: Tree<NearbyAssetNode>) {
        const defaultSymbol = 'default';
        const highlights: __esri.Graphic[] = [];
        if (node.data.graphic) {
            const highlight = node.data.graphic.clone();
            highlight.symbol = this.highlightSymbols[node.data.graphic.geometry.type] || this.highlightSymbols[defaultSymbol];
            highlights.push(highlight);
        }
        const layer = await this.nearbyService.setNearbyLayer(this.nearbyService.nearbyLayers.highlight, highlights);
        this.layerService.moveLayerToTheTop(layer);
        this.layerService.moveLayerToTheTop(this.nearbyService.getNearbyLayer(this.nearbyService.nearbyLayers.mapPin));
    }

    public orderByChange(orderBy) {
        this.orderBy = orderBy;
        this.sortChildren(this.currentNode, orderBy, this.asc);
    }

    public changeOrder() {
        this.asc = !this.asc;
        this.sortChildren(this.currentNode, this.orderBy, this.asc);
    }

    private sortChildren(node, orderBy: string, asc: boolean) {
        this.groups = null;
        const sortOrder = asc ? 1 : -1;
        const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
        if (orderBy) {
            switch (node.children[0].data.nodeType) {
                case NodeType.Record:
                    this.groups = this.buildGroups(node, orderBy);
                    this.groups.sort((a, b) => sortOrder * collator.compare(a[0].split(': ')[1], b[0].split(': ')[1]));
                    break;
                case NodeType.Document:
                    const alias = node.data.columns.find(c => c.name === orderBy).alias;
                    // TODO: Consider making documents data.records the same as Record nodes have.
                    //       Just need data.columnList and change data.records to an array of strings.
                    const groups = node.children.reduce((acc, child) => {
                        (acc[`${alias}: ${child.data.record[orderBy]}`] = acc[`${alias}: ${child.data.record[orderBy]}`] || []).push(child);
                        return acc;
                    }, {});
                    this.groups = Object.entries(groups);
                    this.groups.sort((a, b) => sortOrder * collator.compare(a[0].split(': ')[1], b[0].split(': ')[1]));
                    break;
                default:
                    let idx = node.children[0].data.columnList.indexOf(orderBy);
                    if (idx <= 0) {
                        const column = node.data.columns.find(c => c.alias = orderBy);
                        idx = node.children[0].data.columnList.indexOf(column.name);
                    }
                    node.children.sort((a, b) => sortOrder * collator.compare(a.data.record[idx], b.data.record[idx]));
            }
        } else {
            node.children.sort((a, b) => sortOrder * collator.compare(a.name, b.name));
        }
        this.currentNode.data.asc = asc;
        this.currentNode.data.orderBy = orderBy;
        this.cdr.detectChanges();
    }

    private buildGroups(node: Tree<NearbyAssetNode>, primaryKey: string) {
        const column = node.data.columns.find(c => c.name === primaryKey);
        const alias = column ? column.alias : primaryKey;
        const groups = node.children.reduce((acc, child) => {
            const map = child.data.columnList.reduce((map, column, idx) => {
                map[column] = child.data.record[idx];
                return map
            }, {});
            (acc[`${alias}: ${map[primaryKey]}`] = acc[`${alias}: ${map[primaryKey]}`] || []).push(child);
            return acc;
        }, {});
        return Object.entries(groups);
    }

}
